import type { EventToken } from "@microsoft/wv2-interop";
import type { FrameInfo, HighMemoryInfo, FrameInfoV2, HighMemoryInfoV2, IframeState, } from "@microsoft/wv2-outlook";
import { lazyGetGetFrameInfos, lazyGetGetFrameInfosV2, lazyGetAddOnHighMemoryEventEventHandler, lazyGetRemoveOnHighMemoryEventEventHandler, lazyGetAddOnIframeCreatedEventHandler, lazyGetRemoveOnIframeCreatedEventHandler, lazyGetAddOnIframeDestroyedEventHandler, lazyGetRemoveOnIframeDestroyedEventHandler, lazyGetAddOnHighMemoryEventV2EventHandler, lazyGetRemoveOnHighMemoryEventV2EventHandler, lazyGetAddOnIframeCreatedV2EventHandler, lazyGetRemoveOnIframeCreatedV2EventHandler, lazyGetAddOnIframeDestroyedV2EventHandler, lazyGetRemoveOnIframeDestroyedV2EventHandler, lazyGetNotifyIframeStateChange, } from "./lazyFunctions";

// Types — zero runtime cost
/* eslint-disable owa-custom-rules/forbid-package-reexport  -- (https://aka.ms/OWALintWiki)
 * Re-exporting types from @microsoft/wv2-outlook so consumers don't need a direct dependency. */
export type { FrameInfo, HighMemoryInfo, FrameInfoV2, HighMemoryInfoV2, IframeState, } from "@microsoft/wv2-outlook";
/* eslint-enable owa-custom-rules/forbid-package-reexport */

// --- getFrameInfos ---

async function getGetFrameInfos(): Promise<(() => Promise<FrameInfo[]>) | undefined> {
    return lazyGetGetFrameInfos.importAndExecute();
}

export async function canCallGetFrameInfos(): Promise<boolean> {
    return (await getGetFrameInfos()) !== undefined;
}

export async function getFrameInfos(): Promise<FrameInfo[]> {
    const fn = await getGetFrameInfos();
    if (!fn) {
        throw new Error("getFrameInfos interop API is not available");
    }
    return fn();
}

// --- getFrameInfosV2 ---

async function getGetFrameInfosV2(): Promise<(() => Promise<FrameInfoV2[]>) | undefined> {
    return lazyGetGetFrameInfosV2.importAndExecute();
}

export async function canCallGetFrameInfosV2(): Promise<boolean> {
    return (await getGetFrameInfosV2()) !== undefined;
}

export async function getFrameInfosV2(): Promise<FrameInfoV2[]> {
    const fn = await getGetFrameInfosV2();
    if (!fn) {
        throw new Error("getFrameInfosV2 interop API is not available");
    }
    return fn();
}

// --- addOnHighMemoryEventEventHandler ---

async function getAddOnHighMemoryEventEventHandler(): Promise<((eventHandler: (eventArg: HighMemoryInfo) => Promise<void>) => EventToken) | undefined> {
    return lazyGetAddOnHighMemoryEventEventHandler.importAndExecute();
}

export async function canCallAddOnHighMemoryEventEventHandler(): Promise<boolean> {
    return (await getAddOnHighMemoryEventEventHandler()) !== undefined;
}

export async function addOnHighMemoryEventEventHandler(eventHandler: (eventArg: HighMemoryInfo) => Promise<void>): Promise<EventToken> {
    const fn = await getAddOnHighMemoryEventEventHandler();
    if (!fn) {
        throw new Error("addOnHighMemoryEventEventHandler interop API is not available");
    }
    return fn(eventHandler);
}

// --- removeOnHighMemoryEventEventHandler ---

async function getRemoveOnHighMemoryEventEventHandler(): Promise<((eventToken: EventToken) => void) | undefined> {
    return lazyGetRemoveOnHighMemoryEventEventHandler.importAndExecute();
}

export async function canCallRemoveOnHighMemoryEventEventHandler(): Promise<boolean> {
    return (await getRemoveOnHighMemoryEventEventHandler()) !== undefined;
}

export async function removeOnHighMemoryEventEventHandler(eventToken: EventToken): Promise<void> {
    const fn = await getRemoveOnHighMemoryEventEventHandler();
    if (!fn) {
        throw new Error("removeOnHighMemoryEventEventHandler interop API is not available");
    }
    fn(eventToken);
}

// --- addOnIframeCreatedEventHandler ---

async function getAddOnIframeCreatedEventHandler(): Promise<((eventHandler: (eventArg: FrameInfo[]) => Promise<void>) => EventToken) | undefined> {
    return lazyGetAddOnIframeCreatedEventHandler.importAndExecute();
}

export async function canCallAddOnIframeCreatedEventHandler(): Promise<boolean> {
    return (await getAddOnIframeCreatedEventHandler()) !== undefined;
}

export async function addOnIframeCreatedEventHandler(eventHandler: (eventArg: FrameInfo[]) => Promise<void>): Promise<EventToken> {
    const fn = await getAddOnIframeCreatedEventHandler();
    if (!fn) {
        throw new Error("addOnIframeCreatedEventHandler interop API is not available");
    }
    return fn(eventHandler);
}

// --- removeOnIframeCreatedEventHandler ---

async function getRemoveOnIframeCreatedEventHandler(): Promise<((eventToken: EventToken) => void) | undefined> {
    return lazyGetRemoveOnIframeCreatedEventHandler.importAndExecute();
}

export async function canCallRemoveOnIframeCreatedEventHandler(): Promise<boolean> {
    return (await getRemoveOnIframeCreatedEventHandler()) !== undefined;
}

export async function removeOnIframeCreatedEventHandler(eventToken: EventToken): Promise<void> {
    const fn = await getRemoveOnIframeCreatedEventHandler();
    if (!fn) {
        throw new Error("removeOnIframeCreatedEventHandler interop API is not available");
    }
    fn(eventToken);
}

// --- addOnIframeDestroyedEventHandler ---

async function getAddOnIframeDestroyedEventHandler(): Promise<((eventHandler: (eventArg: FrameInfo[]) => Promise<void>) => EventToken) | undefined> {
    return lazyGetAddOnIframeDestroyedEventHandler.importAndExecute();
}

export async function canCallAddOnIframeDestroyedEventHandler(): Promise<boolean> {
    return (await getAddOnIframeDestroyedEventHandler()) !== undefined;
}

export async function addOnIframeDestroyedEventHandler(eventHandler: (eventArg: FrameInfo[]) => Promise<void>): Promise<EventToken> {
    const fn = await getAddOnIframeDestroyedEventHandler();
    if (!fn) {
        throw new Error("addOnIframeDestroyedEventHandler interop API is not available");
    }
    return fn(eventHandler);
}

// --- removeOnIframeDestroyedEventHandler ---

async function getRemoveOnIframeDestroyedEventHandler(): Promise<((eventToken: EventToken) => void) | undefined> {
    return lazyGetRemoveOnIframeDestroyedEventHandler.importAndExecute();
}

export async function canCallRemoveOnIframeDestroyedEventHandler(): Promise<boolean> {
    return (await getRemoveOnIframeDestroyedEventHandler()) !== undefined;
}

export async function removeOnIframeDestroyedEventHandler(eventToken: EventToken): Promise<void> {
    const fn = await getRemoveOnIframeDestroyedEventHandler();
    if (!fn) {
        throw new Error("removeOnIframeDestroyedEventHandler interop API is not available");
    }
    fn(eventToken);
}

// --- addOnHighMemoryEventV2EventHandler ---

async function getAddOnHighMemoryEventV2EventHandler(): Promise<((eventHandler: (eventArg: HighMemoryInfoV2[]) => Promise<void>) => EventToken) | undefined> {
    return lazyGetAddOnHighMemoryEventV2EventHandler.importAndExecute();
}

export async function canCallAddOnHighMemoryEventV2EventHandler(): Promise<boolean> {
    return (await getAddOnHighMemoryEventV2EventHandler()) !== undefined;
}

export async function addOnHighMemoryEventV2EventHandler(eventHandler: (eventArg: HighMemoryInfoV2[]) => Promise<void>): Promise<EventToken> {
    const fn = await getAddOnHighMemoryEventV2EventHandler();
    if (!fn) {
        throw new Error("addOnHighMemoryEventV2EventHandler interop API is not available");
    }
    return fn(eventHandler);
}

// --- removeOnHighMemoryEventV2EventHandler ---

async function getRemoveOnHighMemoryEventV2EventHandler(): Promise<((eventToken: EventToken) => void) | undefined> {
    return lazyGetRemoveOnHighMemoryEventV2EventHandler.importAndExecute();
}

export async function canCallRemoveOnHighMemoryEventV2EventHandler(): Promise<boolean> {
    return (await getRemoveOnHighMemoryEventV2EventHandler()) !== undefined;
}

export async function removeOnHighMemoryEventV2EventHandler(eventToken: EventToken): Promise<void> {
    const fn = await getRemoveOnHighMemoryEventV2EventHandler();
    if (!fn) {
        throw new Error("removeOnHighMemoryEventV2EventHandler interop API is not available");
    }
    fn(eventToken);
}

// --- addOnIframeCreatedV2EventHandler ---

async function getAddOnIframeCreatedV2EventHandler(): Promise<((eventHandler: (eventArg: FrameInfoV2[]) => Promise<void>) => EventToken) | undefined> {
    return lazyGetAddOnIframeCreatedV2EventHandler.importAndExecute();
}

export async function canCallAddOnIframeCreatedV2EventHandler(): Promise<boolean> {
    return (await getAddOnIframeCreatedV2EventHandler()) !== undefined;
}

export async function addOnIframeCreatedV2EventHandler(eventHandler: (eventArg: FrameInfoV2[]) => Promise<void>): Promise<EventToken> {
    const fn = await getAddOnIframeCreatedV2EventHandler();
    if (!fn) {
        throw new Error("addOnIframeCreatedV2EventHandler interop API is not available");
    }
    return fn(eventHandler);
}

// --- removeOnIframeCreatedV2EventHandler ---

async function getRemoveOnIframeCreatedV2EventHandler(): Promise<((eventToken: EventToken) => void) | undefined> {
    return lazyGetRemoveOnIframeCreatedV2EventHandler.importAndExecute();
}

export async function canCallRemoveOnIframeCreatedV2EventHandler(): Promise<boolean> {
    return (await getRemoveOnIframeCreatedV2EventHandler()) !== undefined;
}

export async function removeOnIframeCreatedV2EventHandler(eventToken: EventToken): Promise<void> {
    const fn = await getRemoveOnIframeCreatedV2EventHandler();
    if (!fn) {
        throw new Error("removeOnIframeCreatedV2EventHandler interop API is not available");
    }
    fn(eventToken);
}

// --- addOnIframeDestroyedV2EventHandler ---

async function getAddOnIframeDestroyedV2EventHandler(): Promise<((eventHandler: (eventArg: FrameInfoV2[]) => Promise<void>) => EventToken) | undefined> {
    return lazyGetAddOnIframeDestroyedV2EventHandler.importAndExecute();
}

export async function canCallAddOnIframeDestroyedV2EventHandler(): Promise<boolean> {
    return (await getAddOnIframeDestroyedV2EventHandler()) !== undefined;
}

export async function addOnIframeDestroyedV2EventHandler(eventHandler: (eventArg: FrameInfoV2[]) => Promise<void>): Promise<EventToken> {
    const fn = await getAddOnIframeDestroyedV2EventHandler();
    if (!fn) {
        throw new Error("addOnIframeDestroyedV2EventHandler interop API is not available");
    }
    return fn(eventHandler);
}

// --- removeOnIframeDestroyedV2EventHandler ---

async function getRemoveOnIframeDestroyedV2EventHandler(): Promise<((eventToken: EventToken) => void) | undefined> {
    return lazyGetRemoveOnIframeDestroyedV2EventHandler.importAndExecute();
}

export async function canCallRemoveOnIframeDestroyedV2EventHandler(): Promise<boolean> {
    return (await getRemoveOnIframeDestroyedV2EventHandler()) !== undefined;
}

export async function removeOnIframeDestroyedV2EventHandler(eventToken: EventToken): Promise<void> {
    const fn = await getRemoveOnIframeDestroyedV2EventHandler();
    if (!fn) {
        throw new Error("removeOnIframeDestroyedV2EventHandler interop API is not available");
    }
    fn(eventToken);
}

// --- notifyIframeStateChange ---

async function getNotifyIframeStateChange(): Promise<((correlationId: string, iframeState: IframeState) => Promise<void>) | undefined> {
    return lazyGetNotifyIframeStateChange.importAndExecute();
}

export async function canCallNotifyIframeStateChange(): Promise<boolean> {
    return (await getNotifyIframeStateChange()) !== undefined;
}

export async function notifyIframeStateChange(correlationId: string, iframeState: IframeState): Promise<void> {
    const fn = await getNotifyIframeStateChange();
    if (!fn) {
        throw new Error("notifyIframeStateChange interop API is not available");
    }
    return fn(correlationId, iframeState);
}
